import {
  GymTypes, IMetricResult, NotFoundError, RemovedReasonType,
} from 'fitbeat.models';
import { Winston } from 'winston';
import { GymSchedulingConfig } from '../../config/gymscheduling';
import { UserWorkoutSession } from '../../entities/workoutsession';
import { DbGymRepository } from '../../persistence/repositories/db/db-gym-repository';
import { IUserProfileRepository } from '../../persistence/repositories/interfaces/iuserprofile-repository';
import { IWorkoutRepository } from '../../persistence';
import { MarketingAnalyticsService } from '../marketing-analytics';
import { IGymScheduler } from '../gymscheduling/igymscheduling';
import { ChallengeManager } from '../challenge/challengeManager';
import { WorkoutOverlapConflictHandling } from './WorkoutOverlapConflictHandling';

/**
 * processing done after workout ended, like completing, adding leaderboards, sending notifications, etc
 */
export class WorkoutCompletionProcessing {
  private readonly workoutOverlapConflictHandling: WorkoutOverlapConflictHandling;

  constructor(
    private readonly workoutRepository: IWorkoutRepository,
    private readonly userProfileRepository: IUserProfileRepository,
    private readonly marketingAnalyticsService: MarketingAnalyticsService,
    private readonly challengeManager: ChallengeManager,
    private readonly gymScheduler: IGymScheduler,
    private readonly logger: Winston,
  ) {
    this.workoutOverlapConflictHandling = new WorkoutOverlapConflictHandling(this.workoutRepository, this.logger);
  }

  /**
   * This is expected to be run when a user ends a home workout.
   * Or when it finishes on a running app.
   */
  public async CompleteHomeWorkout(
    workoutId: string,
    metricResult: IMetricResult | undefined,
    dateOfRequest: Date,
  ): Promise<UserWorkoutSession> {
    const workout = await this.workoutRepository.getWorkoutSessionFromId(workoutId);
    if (!workout) {
      throw NotFoundError.Workout(workoutId);
    }

    const timeUserEndedWorkout = dateOfRequest;
    workout.endTime = timeUserEndedWorkout;

    if (!workout.performanceMetrics && metricResult !== undefined) {
      workout.performanceMetrics = metricResult;
    }

    const completed = await this.handleOverlapsAndCompleteWorkout(workout.userProfileId, workout, dateOfRequest);

    this.postCompleteHomeWorkoutAsync(completed).catch((error) => {
      this.logger.error('Error in background home workout completion post processing', error);
    });
    return completed;
  }

  /**
   * This is expected to be run when a warmdown starts on home app
   * Early save of the workout in case the user closes it without ending.
   */
  public async onHomeWarmdownStarted(workoutId: string, dateOfRequest: Date = new Date()) {
    const workout = await this.workoutRepository.getWorkoutSessionFromId(workoutId);
    if (!workout) {
      throw NotFoundError.Workout(workoutId);
    }
    await this.handleOverlapsAndCompleteWorkout(workout.userProfileId, workout, dateOfRequest);
  }

  /**
   * This is expected to be run in a cron frequently.
   */
  public async ProcessRecentlyEndedSessions(requestDate: Date): Promise<{ home: number, studio: number }> {
    const incompleteWorkouts = await this.workoutRepository.getNotCompletedNotRemovedWorkouts(requestDate);

    const homeWorkoutsRemoved = await this.removeIncompleteHomeWorkouts(incompleteWorkouts, requestDate);

    const studioWorkoutsCompleted = await this.CompleteStudioWorkouts(incompleteWorkouts, requestDate);

    return {
      home: homeWorkoutsRemoved.length,
      studio: studioWorkoutsCompleted.length,
    };
  }

  public async handleOverlapsAndCompleteImport(
    userId: string,
    workout: UserWorkoutSession,
    requestDate: Date,
  ): Promise<void> {
    await this.handleOverlapsAndCompleteWorkout(userId, workout, requestDate);
  }

  /**
   * Contains the common logic for any workout completion.
   * All workouts, home studio or imported, that are completed should be processed here.
   */
  private async processAndCompleteWorkoutWithoutSaving(
    workout: UserWorkoutSession,
    dateOfRequest: Date,
  ): Promise<void> {
    try {
      workout.leaderboards = await this.challengeManager.getLeaderboardInstancesForWorkout(workout, dateOfRequest);
    } catch (e) {
      this.logger.error(`Failed to set leaderboards for workout ${workout.uniqueReference}`, e);
      // don't rethrow so that workout still completes
    }
    // todo gamification achievements etc can be done here later

    workout.workoutMarkedAsCompletedAt = dateOfRequest;
  }

  private async handleOverlapsAndCompleteWorkout(
    userId: string,
    workout: UserWorkoutSession,
    requestDate: Date,
  ): Promise<UserWorkoutSession> {
    const { winner, losers } = await this.workoutOverlapConflictHandling.findOverlapWinnerAndLosers(userId, workout);

    await this.processAndCompleteWorkoutWithoutSaving(winner, requestDate);

    this.removeWorkoutsWithoutSaving(losers, requestDate, winner);

    const completedAndRemovedWorkouts = [winner, ...losers];
    await this.workoutRepository.bulkUpdate(completedAndRemovedWorkouts);

    this.logger.info('Workout overlap and completion processing done', {
      completed: winner.uniqueReference,
      removed: losers.map((loser) => loser.uniqueReference),
      userId,
    });

    return winner;
  }

  private removeWorkoutsWithoutSaving(
    losers: UserWorkoutSession[],
    requestDate: Date,
    winner: UserWorkoutSession,
  ) {
    for (const loser of losers) {
      loser.removedAt = requestDate;
      loser.removedReason = RemovedReasonType.Overlapping;
      loser.overlappedByWorkoutId = winner.uniqueReference;
    }
  }

  private isHomeGym(gymNameToCheck: string): boolean {
    const homeGymNames = DbGymRepository.getGymNames(GymTypes.Home);
    return homeGymNames.find((gymName) => gymName === gymNameToCheck) !== undefined;
  }

  private async removeIncompleteHomeWorkouts(
    incompleteSessions: UserWorkoutSession[],
    requestDate: Date,
  ) {
    const shouldNonCompletedHomeWorkoutBeRemoved = (session: UserWorkoutSession, dateOfRequest: Date): boolean => {
      // home workouts should be removed after a while if they were not completed on the app
      const timeToSubtract = GymSchedulingConfig.timeAfterHomeWorkoutEndToRemoveIncompleteInMS;
      const minutesAgoAllowed = new Date(dateOfRequest.getTime() - timeToSubtract);
      return !!session.endTime && session.endTime.getTime() < minutesAgoAllowed.getTime();
    };
    const homeSessionsToRemove = incompleteSessions.filter(
      (session) => this.isHomeGym(session.gymName)
        && shouldNonCompletedHomeWorkoutBeRemoved(session, requestDate),
    );
    await this.removeSessionsNotCompleted(homeSessionsToRemove, requestDate);
    return homeSessionsToRemove;
  }

  private async postCompleteHomeWorkoutAsync(completed: UserWorkoutSession): Promise<void> {
    const userProfile = await this.userProfileRepository.get(completed.userProfileId);
    if (!userProfile) {
      this.logger.warn(`User profile not found for workout completion: ${completed.userProfileId}`);
      return;
    }

    const workoutCount = await this.workoutRepository.getUserWorkoutCountForGym(
      completed.userProfileId,
      completed.gymName,
    );
    const eventData = {
      workoutCount,
    };

    this.marketingAnalyticsService.logEventInBackground('workout_completed', userProfile, eventData);
  }

  private async CompleteStudioWorkouts(
    incompleteSessions: UserWorkoutSession[],
    requestDate: Date,
  ) {
    const studioSessionsToComplete = incompleteSessions.filter(
      (session) => !this.isHomeGym(session.gymName),
    );

    for (const workout of studioSessionsToComplete) {
      try {
        await this.CompleteStudioWorkout(workout, requestDate);
      } catch (e) {
        this.logger.error(`Failed to complete processing for studio workout ${workout.uniqueReference}`, e);
      }
    }
    return studioSessionsToComplete;
  }

  private async CompleteStudioWorkout(
    workout: UserWorkoutSession,
    dateOfRequest: Date,
  ): Promise<void> {
    await this.handleOverlapsAndCompleteWorkout(workout.userProfileId, workout, dateOfRequest);
    await this.sendStudioWorkoutCompletedEvent(workout)
      .catch((e) => this.logger.error(`Failed to send workout completed event for ${workout.uniqueReference}`, e));
  }

  private async sendStudioWorkoutCompletedEvent(session: UserWorkoutSession): Promise<void> {
    const userProfile = await this.userProfileRepository.get(session.userProfileId);
    if (!userProfile) {
      this.logger.warn(`UserProfile ${session.userProfileId} not found – skipping event`);
      return;
    }
    const workoutCount = await this.workoutRepository.getUserWorkoutCountForGym(
      session.userProfileId,
      session.gymName,
    );
    const eventData = {
      workoutCount,
    };
    this.marketingAnalyticsService?.logEventInBackground('workout_completed', userProfile, eventData);
  }

  private async removeSessionsNotCompleted(
    checkedInNotCompletedSessions: UserWorkoutSession[],
    dateOfRequest: Date,
  ): Promise<void> {
    const notCompletedSessions: (UserWorkoutSession | undefined)[] = await Promise.all(
      checkedInNotCompletedSessions.map((session: UserWorkoutSession) => this.gymScheduler.RemoveWorkoutSession(
        dateOfRequest,
        session,
        RemovedReasonType.NotCompleted,
      )),
    );

    const removedSessions: UserWorkoutSession[] = notCompletedSessions
      .filter((session) => session !== undefined) as UserWorkoutSession[];
    removedSessions.forEach((session: UserWorkoutSession) => {
      this.logger.info(`session removed because it was not completed: ${session.uniqueReference}, user:`
            + `${session.userProfileId}`);
    });
  }
}
